import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { html } from 'lit';
import type { Company } from '../db/company.js';
import {
  buildUserGreeting,
  companyRejectedByUserMailTemplate,
  MailTemplateType,
  MailTemplateVars,
  statusChangedAcceptedMailTemplate,
  statusChangedRejectedMailTemplate,
  statusChangedWaitingMailTemplate,
} from './mail-templates.js';

export const CandidateAcceptedMail = defineMailTemplate(MailTemplateType.STATUS_CHANGED_ACCEPTED, {
  displayName: 'Bewerbung akzeptiert',
  //TODO: for some reason mailer does not care about defaultTemplate -> and we need to ensure it on startup
  defaultTemplate: statusChangedAcceptedMailTemplate,
  variables: [
    MailTemplateVars.CANDIDATE_FULL_NAME,
    MailTemplateVars.CANDIDATE_FIRST_NAME,
    MailTemplateVars.CANDIDATE_LAST_NAME,
    MailTemplateVars.JOB_TITLE,
    MailTemplateVars.BOOK_APPOINTMENT_LINK,
    MailTemplateVars.COMPANY_NAME,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Kandidat:in<br />
    </p>
    <p>
      Wann:<br />
      - Wenn Kandidat:in akzeptiert wurde und sich einen Termin für das Vorstellungsgespräch buchen kann
    </p>
  `,
  getMailData: (
    user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    jobTitle: string,
    bookAppointmentLink: string,
    company: EntityData<NarrowedEntity<Company, { name: 1 }>>,
  ) => {
    const greeting = buildUserGreeting(user);
    return {
      data: {
        [MailTemplateVars.CANDIDATE_FULL_NAME]: greeting.fullName,
        [MailTemplateVars.CANDIDATE_FIRST_NAME]: greeting.firstName,
        [MailTemplateVars.CANDIDATE_LAST_NAME]: greeting.lastName,
        [MailTemplateVars.JOB_TITLE]: jobTitle,
        [MailTemplateVars.BOOK_APPOINTMENT_LINK]: bookAppointmentLink,
        [MailTemplateVars.COMPANY_NAME]: company.name ?? '---',
      },
      scope: 'default',
    };
  },
});

export const CandidateWaitingListMail = defineMailTemplate(MailTemplateType.STATUS_CHANGED_WAITING, {
  displayName: 'Bewerbung auf Warteliste',
  //TODO: for some reason mailer does not care about defaultTemplate -> and we need to ensure it on startup
  defaultTemplate: statusChangedWaitingMailTemplate,
  variables: [
    MailTemplateVars.CANDIDATE_FULL_NAME,
    MailTemplateVars.CANDIDATE_FIRST_NAME,
    MailTemplateVars.CANDIDATE_LAST_NAME,
    MailTemplateVars.JOB_TITLE,
    MailTemplateVars.COMPANY_NAME,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Kandidat:in<br />
    </p>
    <p>
      Wann:<br />
      - Wenn Kandidat:in auf Warteliste gesetzt wurde
    </p>
  `,
  getMailData: (
    user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    jobTitle: string,
    company: EntityData<NarrowedEntity<Company, { name: 1 }>>,
  ) => {
    const greeting = buildUserGreeting(user);
    return {
      data: {
        [MailTemplateVars.CANDIDATE_FULL_NAME]: greeting.fullName,
        [MailTemplateVars.CANDIDATE_FIRST_NAME]: greeting.firstName,
        [MailTemplateVars.CANDIDATE_LAST_NAME]: greeting.lastName,
        [MailTemplateVars.JOB_TITLE]: jobTitle,
        [MailTemplateVars.COMPANY_NAME]: company.name ?? '---',
      },
      scope: 'default',
    };
  },
});

export const CandidateRejectedMail = defineMailTemplate(MailTemplateType.STATUS_CHANGED_REJECTED, {
  displayName: 'Bewerbung abgelehnt',
  //TODO: for some reason mailer does not care about defaultTemplate -> and we need to ensure it on startup
  defaultTemplate: statusChangedRejectedMailTemplate,
  variables: [
    MailTemplateVars.CANDIDATE_FULL_NAME,
    MailTemplateVars.CANDIDATE_FIRST_NAME,
    MailTemplateVars.CANDIDATE_LAST_NAME,
    MailTemplateVars.JOB_TITLE,
    MailTemplateVars.COMPANY_NAME,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Kandidat:in<br />
    </p>
    <p>
      Wann:<br />
      - Wenn Kandidat:in abgelehnt wurde
    </p>
  `,
  getMailData: (
    user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    jobTitle: string,
    company: EntityData<NarrowedEntity<Company, { name: 1 }>>,
  ) => {
    const greeting = buildUserGreeting(user);
    return {
      data: {
        [MailTemplateVars.CANDIDATE_FULL_NAME]: greeting.fullName,
        [MailTemplateVars.CANDIDATE_FIRST_NAME]: greeting.firstName,
        [MailTemplateVars.CANDIDATE_LAST_NAME]: greeting.lastName,
        [MailTemplateVars.JOB_TITLE]: jobTitle,
        [MailTemplateVars.COMPANY_NAME]: company.name ?? '---',
      },
      scope: 'default',
    };
  },
});

export const CompanyRejectedByUserMail = defineMailTemplate(MailTemplateType.COMPANY_REJECTED_BY_USER, {
  displayName: 'Gespräch storniert (Unternehmen)',
  // Bug fix: previously referenced `statusChangedRejectedMailTemplate` (the
  // candidate-facing rejection template) as the default — wrong content for a
  // company recipient. The dedicated `companyRejectedByUserMailTemplate` is
  // the right default.
  defaultTemplate: companyRejectedByUserMailTemplate,
  variables: [
    MailTemplateVars.COMPANY_FULL_NAME,
    MailTemplateVars.COMPANY_FIRST_NAME,
    MailTemplateVars.COMPANY_LAST_NAME,
    MailTemplateVars.JOB_TITLE,
    MailTemplateVars.CANDIDATE_FULL_NAME,
    MailTemplateVars.CANDIDATE_FIRST_NAME,
    MailTemplateVars.CANDIDATE_LAST_NAME,
    MailTemplateVars.TIME_SLOT,
    MailTemplateVars.COMPANY_NAME,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Unternehmensnutzer:innen<br />
    </p>
    <p>
      Wann:<br />
      - Wenn Kandidat:in ein Vorstellungsgespräch storniert hat
    </p>
  `,
  getMailData: (
    user: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    jobTitle: string,
    candidate: NarrowedEntity<User, { firstName: 1; lastName: 1; username: 1 }>,
    timeSlot: string,
    company: EntityData<NarrowedEntity<Company, { name: 1 }>>,
  ) => {
    const companyGreeting = buildUserGreeting(user);
    const candidateGreeting = buildUserGreeting(candidate);
    return {
      data: {
        [MailTemplateVars.COMPANY_FULL_NAME]: companyGreeting.fullName,
        [MailTemplateVars.COMPANY_FIRST_NAME]: companyGreeting.firstName,
        [MailTemplateVars.COMPANY_LAST_NAME]: companyGreeting.lastName,
        [MailTemplateVars.JOB_TITLE]: jobTitle ?? '---',
        [MailTemplateVars.CANDIDATE_FULL_NAME]: candidateGreeting.fullName,
        [MailTemplateVars.CANDIDATE_FIRST_NAME]: candidateGreeting.firstName,
        [MailTemplateVars.CANDIDATE_LAST_NAME]: candidateGreeting.lastName,
        [MailTemplateVars.TIME_SLOT]: timeSlot,
        [MailTemplateVars.COMPANY_NAME]: company.name ?? '---',
      },
      scope: 'default',
    };
  },
});
